import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalPersistStorage } from '@/stores/persistStorage';
import { useAuthStore } from '@/stores/authStore';
import { writePattern } from '@/stores/sync';
import type { SkipPattern, UserPattern } from '@/types';

const defaultPattern: UserPattern = {
  peakStart: '09:00',
  peakEnd: '12:00',
  midStart: '14:00',
  midEnd: '17:00',
  lowWindows: ['12:00-13:00', '17:00-19:00'],
  avgCompletionRate: 0,
  skipPatterns: [],
  preferredTaskDuration: 30,
  updatedAt: new Date(0),
};

export interface CompletionEvent {
  hour: number; // 0-23
  date: string; // YYYY-MM-DD
}

export interface EnergyPattern {
  highHours: number[];
  lowHours: number[];
}

export const DEFAULT_ENERGY_PATTERN: EnergyPattern = {
  highHours: [9, 10, 11, 15, 16, 17],
  lowHours: [13, 14, 20, 21, 22],
};

const MIN_COMPLETIONS_FOR_DERIVED = 14;
const RETENTION_DAYS = 90;

export interface UserPatternState {
  pattern: UserPattern;
  completions: CompletionEvent[];
  setPattern: (pattern: UserPattern) => void;
  updatePattern: (patch: Partial<Omit<UserPattern, 'updatedAt'>>) => void;
  setSkipPatterns: (skipPatterns: SkipPattern[]) => void;
  recordCompletion: (event: CompletionEvent) => void;
  /** Clear all learned data — completion history + derived pattern. */
  reset: () => void;
}

function syncPattern(pattern: UserPattern, completions: CompletionEvent[]) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void writePattern(uid, pattern, completions);
  }
}

function daysBetween(a: string, b: string): number {
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  return Math.abs(Math.round((da - db) / 86_400_000));
}

export const useUserPatternStore = create<UserPatternState>()(
  persist(
    (set, get) => ({
      pattern: defaultPattern,
      completions: [],
      setPattern: (pattern) => {
        set({ pattern: { ...pattern, updatedAt: new Date() } });
        const s = get();
        syncPattern(s.pattern, s.completions);
      },
      updatePattern: (patch) => {
        set((s) => ({ pattern: { ...s.pattern, ...patch, updatedAt: new Date() } }));
        const s = get();
        syncPattern(s.pattern, s.completions);
      },
      setSkipPatterns: (skipPatterns) => {
        set((s) => ({
          pattern: { ...s.pattern, skipPatterns, updatedAt: new Date() },
        }));
        const s = get();
        syncPattern(s.pattern, s.completions);
      },
      recordCompletion: (event) => {
        set((s) => {
          const all = [...s.completions, event];
          const latest = all.reduce((max, c) => (c.date > max ? c.date : max), event.date);
          const kept = all.filter((c) => daysBetween(c.date, latest) <= RETENTION_DAYS);
          return { completions: kept };
        });
        const s = get();
        syncPattern(s.pattern, s.completions);
      },
      reset: () => {
        set({ pattern: { ...defaultPattern, updatedAt: new Date() }, completions: [] });
        const s = get();
        syncPattern(s.pattern, s.completions);
      },
    }),
    { name: 'brainday:pattern', storage: conditionalPersistStorage<UserPatternState>() },
  ),
);

export function selectEnergyPattern(state: UserPatternState): EnergyPattern {
  if (state.completions.length < MIN_COMPLETIONS_FOR_DERIVED) {
    return DEFAULT_ENERGY_PATTERN;
  }
  const histogram = new Array<number>(24).fill(0);
  for (const c of state.completions) histogram[c.hour] = (histogram[c.hour] ?? 0) + 1;
  const indices = histogram.map((count, hour) => ({ count, hour }));
  const sortedDesc = [...indices].sort((a, b) => b.count - a.count);
  const highHours = sortedDesc
    .slice(0, 3)
    .map((x) => x.hour)
    .sort((a, b) => a - b);
  const nonZeroAsc = [...indices].filter((x) => x.count > 0).sort((a, b) => a.count - b.count);
  const lowHours = nonZeroAsc
    .slice(0, 3)
    .map((x) => x.hour)
    .sort((a, b) => a - b);
  return { highHours, lowHours };
}
